import { FocusTable, ToolBarItemType } from "@focus/focusUI";
import type {
  IColumnDefsConfig,
  ITableData,
  RowClassRules,
} from "@focus/focusUI/lib/components/Tables/FocusTable/Entities";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { handleTableFilterChange } from "../../helpers/utils";
import { useHashParams } from "../../hooks/useHashParams";
import type { ServerColumnConfig } from "../../services/helpers/headers";
import type {
  ColumnFiltersState,
  SortingState,
} from "../../services/helpers/pager";
import { FiltersTag } from "./FiltersTag";
import { type TableActionType } from "./TableActionColumn";
import { TableSideBar } from "./TableSideBar";
import { useFocusocTable, type OnChangeFn } from "./useFocusocTable";

const getValueByPath = (row: Record<string, unknown>, path: string) =>
  path.split(".").reduce<unknown>((acc, key) => {
    if (acc && typeof acc === "object") {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, row);

const compareValues = (left: unknown, right: unknown) => {
  const leftDate = left ? Date.parse(String(left)) : Number.NaN;
  const rightDate = right ? Date.parse(String(right)) : Number.NaN;

  if (!Number.isNaN(leftDate) && !Number.isNaN(rightDate)) {
    return leftDate - rightDate;
  }

  if (typeof left === "number" && typeof right === "number") {
    return left - right;
  }

  return String(left ?? "").localeCompare(String(right ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

interface InfiniteTableProps {
  columnDefinitions: IColumnDefsConfig[];
  serverDisplayConfig: ServerColumnConfig;
  defaultVisibleValues: string[];
  rowsGetter: (param: any) => Promise<any[]>;
  getNumberOfRows?: (param: object) => Promise<number>;
  source?: string;
  base: string;
  actions: TableActionType[];
  downloadData: (param: { id: string }) => void;
  showOperatorColumn?: boolean;
  showSidebar?: boolean;
  rowClassRules?: RowClassRules;
  controlledColumnFilter: ColumnFiltersState;
  controlledColumnFilterChange: OnChangeFn<ColumnFiltersState>;
  saveColumnLayout?: (serverConfig: ServerColumnConfig) => void;
  refreshKey?: unknown;
  initialSorting?: SortingState;
}

export const InfiniteTable: React.FC<InfiniteTableProps> = ({
  columnDefinitions,
  serverDisplayConfig,
  defaultVisibleValues,
  rowsGetter,
  getNumberOfRows,
  source,
  base,
  actions,
  downloadData,
  showOperatorColumn = true,
  showSidebar = true,
  rowClassRules,
  controlledColumnFilter,
  controlledColumnFilterChange,
  saveColumnLayout,
  refreshKey,
  initialSorting = [],
}) => {
  const { getParam, setParam } = useHashParams();
  const [data, setData] = useState<ITableData[]>([]);
  const [totalRows, setTotalRows] = useState(0);

  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [globalFilter, setGlobalFilter] = useState("");

  // We only need the ref to get the operator of each filter for the FiltersTag
  const selectedFilterFnRef = useRef<Record<string, string>>({});

  const handleChange: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      // This returns the filter with operator property added, which is needed for server side filtering
      const filtered = handleTableFilterChange(
        updater,
        controlledColumnFilter,
        selectedFilterFnRef.current,
      );
      controlledColumnFilterChange(filtered);
    },
    [controlledColumnFilter, controlledColumnFilterChange],
  );

  const initialPageIndex = useMemo(() => {
    const pageFromUrl = Number(getParam("page"));
    return Number.isFinite(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : 0;
  }, []);

  const [pagination, setPagination] = useState({
    pageIndex: initialPageIndex,
    pageSize: 30,
  });

  const {
    columnDefs,
    columnVisibility,
    columnOrder,
    toggleVisibility,
    handleColumnOrderChange,
    handleColumnResize,
  } = useFocusocTable({
    serverDisplayConfig,
    defaultVisibleValues,
    columnDefinitions,
    actions,
    base,
    source,
    downloadData,
    saveColumnLayout,
    showOperatorColumn,
  });

  const sortedData = useMemo(() => {
    if (sorting.length === 0) {
      return data;
    }

    const [{ id, desc }] = sorting;
    return [...data].sort((left, right) => {
      const comparison = compareValues(
        getValueByPath(left as Record<string, unknown>, id),
        getValueByPath(right as Record<string, unknown>, id),
      );

      return desc ? -comparison : comparison;
    });
  }, [data, sorting]);

  useEffect(() => {
    setParam("page", pagination.pageIndex.toString());
  }, [pagination.pageIndex]);

  useEffect(() => {
    const pageFromUrl = Number(getParam("page"));

    if (Number.isFinite(pageFromUrl) && pageFromUrl !== pagination.pageIndex) {
      setPagination((prev) => ({
        ...prev,
        pageIndex: pageFromUrl,
      }));
    }
  }, [getParam("page")]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [numberOfRows, page] = await Promise.all([
          getNumberOfRows?.({ filter: controlledColumnFilter }),
          rowsGetter({
            page: pagination.pageIndex,
            size: pagination.pageSize,
            sort: sorting,
            filter: controlledColumnFilter,
          }),
        ]);

        if (numberOfRows !== undefined) {
          setTotalRows(numberOfRows);
        }

        setData(page);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, [pagination, sorting, controlledColumnFilter, refreshKey]);

  const paginationOptions = useMemo(
    () => ({
      pageCount: Math.ceil(totalRows / pagination.pageSize),
      manualPagination: {
        pagination,
        setPagination,
      },
    }),
    [totalRows, pagination, setPagination],
  );

  const customRightItems = [
    {
      type: ToolBarItemType.Custom,
      component: (
        <FiltersTag
          columnFilters={controlledColumnFilter}
          columnFiltersChange={controlledColumnFilterChange}
          columnDefinitions={columnDefinitions}
        />
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 overflow-hidden">
      <FocusTable
        className="min-w-0 flex-1"
        data={sortedData}
        setData={setData}
        columnDefs={columnDefs}
        getRowId={(row) => row.id}
        paginated
        manualFiltering
        manualSorting
        controlledSorting={sorting}
        controlledSortingChange={setSorting}
        controlledColumnFilters={controlledColumnFilter}
        controlledColumnFilterChange={handleChange}
        paginationOptions={paginationOptions}
        height="auto"
        controlledGlobalFilter={globalFilter}
        controlledGlobalFilterChange={setGlobalFilter}
        controlledColumnVisibility={columnVisibility}
        onColumnResize={handleColumnResize}
        rowClassRules={rowClassRules}
        controlledColumnOrder={columnOrder}
        selectedFilterFnRef={selectedFilterFnRef}
        rightMenuItems={customRightItems as []}
      />
      {showSidebar && (
        <TableSideBar
          columnDefinitions={columnDefs}
          columnVisibility={columnVisibility}
          toggleVisibility={toggleVisibility}
          columnOrder={columnOrder}
          handleColumnOrderChange={handleColumnOrderChange}
        />
      )}
    </div>
  );
};
